/**
 * SlotCoreDownloadService
 *
 * Downloads the pixi-slot repo archive from the ai-proxy backend,
 * extracts it with JSZip, and writes files to the project directory
 * via FileSystemService.
 *
 * Follows the same proxy pattern as GeminiProxyClient.ts.
 */

import JSZip from 'jszip';
import { AuthService } from '../auth/AuthService';
import {
  fileExists,
  readFile,
  writeFile,
  writeFileAsArrayBuffer,
  createDirectory,
  deleteDirectory,
} from '../FileSystemService';
import {
  SLOT_CORE_DEST_FOLDER,
  SLOT_CORE_DEFAULT_REF,
  SLOT_CORE_VERSION_FILE,
} from '../../constants/slot-core';
import type {
  SlotCoreInfo,
  SlotCoreInstalledVersion,
  SlotCoreProgress,
  SlotCoreInstallResult,
  SlotCoreUpdateCheck,
} from './types';

// ─── Proxy Base URL ──────────────────────────────────────

function getProxyBaseUrl(): string {
  return import.meta.env.AI_PROXY_URL
    ? `${import.meta.env.AI_PROXY_URL.replace(/\/api\/ai\/?$/, '')}/api/repo-archive`
    : '/api/repo-archive';
}

function getAuthHeaders(): Record<string, string> {
  const token = AuthService.getStoredToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// ─── Info & Version Checks ───────────────────────────────

/**
 * Get metadata about the available archive from the backend.
 */
export async function getSlotCoreInfo(
  ref = SLOT_CORE_DEFAULT_REF,
): Promise<SlotCoreInfo | null> {
  try {
    const base = getProxyBaseUrl();
    const response = await fetch(`${base}/info?ref=${encodeURIComponent(ref)}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  }
}

/**
 * Read the installed version from the project's slot-core/.version file.
 */
export async function getInstalledVersion(): Promise<SlotCoreInstalledVersion | null> {
  try {
    const versionPath = `${SLOT_CORE_DEST_FOLDER}/${SLOT_CORE_VERSION_FILE}`;
    const exists = await fileExists(versionPath);
    if (!exists) return null;

    const content = await readFile(versionPath);
    return JSON.parse(content);
  } catch {
    return null;
  }
}

/**
 * Check whether slot-core is installed in the current project.
 */
export async function isSlotCoreInstalled(): Promise<boolean> {
  const version = await getInstalledVersion();
  return version !== null;
}

/**
 * Compare local installed SHA with remote latest SHA.
 */
export async function checkForUpdate(
  ref = SLOT_CORE_DEFAULT_REF,
): Promise<SlotCoreUpdateCheck> {
  const [local, remote] = await Promise.all([
    getInstalledVersion(),
    getSlotCoreInfo(ref),
  ]);

  if (!local) {
    return {
      hasUpdate: true,
      remoteSha: remote?.sha,
    };
  }

  if (!remote) {
    return { hasUpdate: false, localSha: local.sha };
  }

  return {
    hasUpdate: local.sha !== remote.sha,
    localSha: local.sha,
    remoteSha: remote.sha,
  };
}

// ─── Download & Install ──────────────────────────────────

/**
 * Download the slot core archive and install it into the project.
 *
 * 1. Fetches zip from backend proxy
 * 2. Extracts with JSZip
 * 3. Writes files to slot-core/ via FileSystemService
 * 4. Writes .version marker
 */
export async function downloadAndInstallSlotCore(
  onProgress?: (progress: SlotCoreProgress) => void,
  signal?: AbortSignal,
  ref = SLOT_CORE_DEFAULT_REF,
): Promise<SlotCoreInstallResult> {
  try {
    // Step 1: Fetch the zip
    const base = getProxyBaseUrl();
    const response = await fetch(
      `${base}/download?ref=${encodeURIComponent(ref)}`,
      {
        headers: getAuthHeaders(),
        signal,
      },
    );

    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      let message = `Download failed (${response.status})`;
      try {
        const parsed = JSON.parse(errorBody);
        message = parsed.message || message;
      } catch {
        if (errorBody) message = errorBody;
      }
      return { success: false, error: message };
    }

    const sha = response.headers.get('X-Repo-Sha') || 'unknown';
    const totalBytes = parseInt(response.headers.get('Content-Length') || '0', 10);

    // Step 2: Read response with progress tracking
    const buffer = await readResponseWithProgress(
      response,
      totalBytes,
      onProgress,
      signal,
    );

    // Step 3: Extract zip
    onProgress?.({
      phase: 'extracting',
      filesProcessed: 0,
      totalFiles: 0,
      message: 'Extracting archive...',
    });

    const zip = await JSZip.loadAsync(buffer);
    const entries = Object.entries(zip.files).filter(([, file]) => !file.dir);
    const totalFiles = entries.length;

    onProgress?.({
      phase: 'extracting',
      filesProcessed: 0,
      totalFiles,
    });

    // Step 4: Delete existing slot-core/ if present (clean install/update)
    try {
      const exists = await fileExists(`${SLOT_CORE_DEST_FOLDER}/${SLOT_CORE_VERSION_FILE}`);
      if (exists) {
        await deleteDirectory(SLOT_CORE_DEST_FOLDER, true);
      }
    } catch {
      // Ignore — directory may not exist
    }

    // Step 5: Write files
    // GitHub zips have a top-level directory like "owner-repo-sha/"
    // We need to strip this prefix
    const topLevelPrefix = findTopLevelPrefix(zip);
    let filesWritten = 0;

    // Collect unique directories first
    const dirs = new Set<string>();
    for (const [path] of entries) {
      const relativePath = stripPrefix(path, topLevelPrefix);
      if (!relativePath) continue;
      const destPath = `${SLOT_CORE_DEST_FOLDER}/${relativePath}`;
      const dirPath = destPath.substring(0, destPath.lastIndexOf('/'));
      if (dirPath) dirs.add(dirPath);
    }

    // Create all directories
    for (const dir of dirs) {
      await createDirectory(dir, true);
    }

    // Write all files
    for (const [path, file] of entries) {
      if (signal?.aborted) {
        return { success: false, error: 'Installation cancelled' };
      }

      const relativePath = stripPrefix(path, topLevelPrefix);
      if (!relativePath) continue;

      const destPath = `${SLOT_CORE_DEST_FOLDER}/${relativePath}`;
      const content = await file.async('uint8array');
      await writeFileAsArrayBuffer(destPath, content);

      filesWritten++;
      if (filesWritten % 5 === 0 || filesWritten === totalFiles) {
        onProgress?.({
          phase: 'extracting',
          filesProcessed: filesWritten,
          totalFiles,
        });
      }
    }

    // Step 6: Write version marker
    const versionData: SlotCoreInstalledVersion = {
      sha,
      ref,
      installedAt: new Date().toISOString(),
      filesCount: filesWritten,
    };
    await writeFile(
      `${SLOT_CORE_DEST_FOLDER}/${SLOT_CORE_VERSION_FILE}`,
      JSON.stringify(versionData, null, 2),
    );

    onProgress?.({
      phase: 'complete',
      filesProcessed: filesWritten,
      totalFiles: filesWritten,
      message: 'Installation complete',
    });

    return { success: true, filesWritten, sha };
  } catch (err) {
    if ((err as Error).name === 'AbortError') {
      return { success: false, error: 'Installation cancelled' };
    }
    const message = err instanceof Error ? err.message : 'Unknown error';
    onProgress?.({ phase: 'error', message });
    return { success: false, error: message };
  }
}

// ─── Helpers ─────────────────────────────────────────────

async function readResponseWithProgress(
  response: Response,
  totalBytes: number,
  onProgress?: (progress: SlotCoreProgress) => void,
  signal?: AbortSignal,
): Promise<Uint8Array> {
  if (!response.body) {
    const arrayBuffer = await response.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let bytesReceived = 0;

  while (true) {
    if (signal?.aborted) {
      reader.cancel();
      throw new DOMException('Aborted', 'AbortError');
    }

    const { done, value } = await reader.read();
    if (done) break;

    chunks.push(value);
    bytesReceived += value.length;

    onProgress?.({
      phase: 'downloading',
      bytesReceived,
      totalBytes: totalBytes || undefined,
    });
  }

  // Concatenate chunks
  const result = new Uint8Array(bytesReceived);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

function findTopLevelPrefix(zip: JSZip): string {
  const paths = Object.keys(zip.files);
  if (paths.length === 0) return '';

  // GitHub zips always have a single top-level directory
  const first = paths[0];
  const slashIdx = first.indexOf('/');
  if (slashIdx === -1) return '';

  const prefix = first.substring(0, slashIdx + 1);

  // Verify all entries share this prefix
  const allMatch = paths.every((p) => p.startsWith(prefix));
  return allMatch ? prefix : '';
}

function stripPrefix(path: string, prefix: string): string {
  if (!prefix) return path;
  if (!path.startsWith(prefix)) return path;
  return path.substring(prefix.length);
}
